import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import type { FormField, FormValues } from '@/content/forms';
import { buildMailBody, buildMailSubject } from '@/content/forms';
import { BRAND } from '@/content/site';
import { buildMailtoHref, copyText, isMailtoUsable } from '@/lib/mailto';
import { Button, buttonClass } from './Button';
import { Icon } from './Icon';

interface MailFormProps {
  id?: string;
  fields: readonly FormField[];
  /** 邮件正文开头的一行，例如「试用申请」。 */
  heading: string;
  subjectPrefix: string;
  submitLabel?: string;
  className?: string;
}

interface Ready {
  subject: string;
  body: string;
  href: string;
  mailClientUsable: boolean;
}

/**
 * 表单的唯一产出是一封邮件：填完在本地拼好，交给邮箱客户端或剪贴板。
 * 因此没有"已提交"状态，也不会有任何东西被发到服务器。
 */
export function MailForm({ id, fields, heading, subjectPrefix, submitLabel = '生成邮件', className }: MailFormProps) {
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [ready, setReady] = useState<Ready | null>(null);
  const [copied, setCopied] = useState(false);

  const setField = useCallback((name: string, value: string) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => (previous[name] ? { ...previous, [name]: '' } : previous));
    setReady(null);
    setCopied(false);
  }, []);

  const validate = useCallback((): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const field of fields) {
      const value = (values[field.name] ?? '').trim();
      if (field.required && value === '') {
        next[field.name] = `请填写${field.label}`;
        continue;
      }
      if (field.type === 'email' && value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        next[field.name] = '邮箱格式看起来不对';
      }
    }
    return next;
  }, [fields, values]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const found = validate();
      setErrors(found);
      setCopied(false);

      if (Object.keys(found).length > 0) {
        setReady(null);
        return;
      }

      const subject = buildMailSubject(subjectPrefix, values);
      const body = buildMailBody(heading, fields, values);
      const href = buildMailtoHref(subject, body);

      setReady({ subject, body, href, mailClientUsable: isMailtoUsable(href) });

      if (isMailtoUsable(href)) {
        // 用户手势内跳转到 mailto：由浏览器决定唤起哪个客户端。
        window.location.href = href;
      }
    },
    [fields, heading, subjectPrefix, validate, values],
  );

  const handleCopy = useCallback(async () => {
    if (!ready) return;
    const text = `收件人：${BRAND.email}\n主题：${ready.subject}\n\n${ready.body}`;
    setCopied(await copyText(text));
  }, [ready]);

  const gridColumns = 'grid gap-4 md:grid-cols-2';

  return (
    <form id={id} onSubmit={handleSubmit} noValidate className={['flex flex-col gap-5', className].filter(Boolean).join(' ')}>
      <div className={gridColumns}>
        {fields.map((field) => (
          <FieldControl
            key={field.name}
            field={field}
            value={values[field.name] ?? ''}
            error={errors[field.name]}
            onChange={setField}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" size="md">
          <Icon name="mail" size={16} />
          {submitLabel}
        </Button>
        {ready ? (
          <Button type="button" variant="secondary" size="md" onClick={handleCopy}>
            <Icon name={copied ? 'check' : 'copy'} size={16} />
            {copied ? '已复制正文' : '复制邮件正文'}
          </Button>
        ) : null}
      </div>

      <p className="text-xs leading-relaxed text-fg-muted">
        表单不会将任何内容发送至服务器：点击上方按钮后，浏览器将在本地拼装一封邮件并交由您自己的邮箱客户端。
      </p>

      {ready ? (
        <div className="rounded-xl border border-line bg-surface-2 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-medium text-fg">
            <Icon name="check" size={16} />
            邮件已在您的客户端中打开
          </p>
          <dl className="mb-3 space-y-1 text-xs text-fg-muted">
            <div className="flex gap-2">
              <dt className="min-w-12 text-fg-subtle">收件人</dt>
              <dd className="metric-value">{BRAND.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="min-w-12 text-fg-subtle">主题</dt>
              <dd>{ready.subject}</dd>
            </div>
          </dl>
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-surface-1 p-3 text-xs leading-relaxed text-fg-muted">
            {ready.body}
          </pre>
          {ready.mailClientUsable ? (
            <a href={ready.href} className={buttonClass('ghost', 'sm', 'mt-3')}>
              没打开？再点一次
              <Icon name="external" size={14} />
            </a>
          ) : (
            <p className="mt-3 text-xs text-fg-muted">
              正文较长，部分邮箱客户端会截断带正文的链接，建议用上面的「复制邮件正文」粘贴发送。
            </p>
          )}
        </div>
      ) : null}
    </form>
  );
}

function FieldControl({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField;
  value: string;
  error?: string;
  onChange: (name: string, value: string) => void;
}) {
  const inputId = `field-${field.name}`;
  const describedBy = [field.hint ? `${inputId}-hint` : null, error ? `${inputId}-error` : null]
    .filter(Boolean)
    .join(' ');

  const controlClass = [
    'w-full rounded-xl border bg-surface-1 px-3.5 py-2.5 text-sm text-fg transition-colors',
    'placeholder:text-fg-subtle focus:border-line-strong focus:outline-none',
    error ? 'border-palette-5' : 'border-line',
  ].join(' ');

  return (
    <div className={['flex flex-col gap-1.5', field.wide ? 'md:col-span-2' : ''].filter(Boolean).join(' ')}>
      <label htmlFor={inputId} className="text-xs font-medium text-fg">
        {field.label}
        {field.required ? <span className="ml-1 text-fg-subtle">*</span> : <span className="ml-1 text-fg-subtle">选填</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          id={inputId}
          name={field.name}
          rows={5}
          value={value}
          placeholder={field.placeholder}
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
          className={[controlClass, 'resize-y'].join(' ')}
        />
      ) : field.type === 'select' ? (
        <select
          id={inputId}
          name={field.name}
          value={value}
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
          className={controlClass}
        >
          <option value="">请选择</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={inputId}
          name={field.name}
          type={field.type}
          value={value}
          placeholder={field.placeholder}
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
          className={controlClass}
        />
      )}

      {field.hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-fg-muted">
          {field.hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${inputId}-error`} className="flex items-center gap-1.5 text-xs text-fg">
          <Icon name="alert" size={13} className="text-fg-muted" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
